import { type ReactNode, useId, useState } from 'react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Checkbox } from '../ui/checkbox'
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar'

export interface MemberOption {
  id: string
  name: string
  email?: string | null
  avatar?: string | null
  description?: string
  disabled?: boolean
}

export function MemberPicker({ options, selected, onChange, disabled = false, renderAction }: {
  options: MemberOption[]
  selected: Set<string>
  onChange: (ids: Set<string>) => void
  disabled?: boolean
  renderAction?: (option: MemberOption) => ReactNode
}) {
  const [search, setSearch] = useState('')
  const searchId = useId()
  const query = search.trim().toLowerCase()
  const matches = options.filter(option => `${option.name} ${option.email ?? ''}`.toLowerCase().includes(query))
  const eligible = matches.filter(option => !option.disabled)
  const eligibleIds = new Set(options.filter(option => !option.disabled).map(option => option.id))
  const count = [...selected].filter(id => eligibleIds.has(id)).length
  return <div className="space-y-2">
    <label htmlFor={searchId} className="text-sm font-medium">Search members</label>
    <Input id={searchId} placeholder="Search by name or email…" value={search} disabled={disabled} onChange={event => setSearch(event.target.value)} />
    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
      <span role="status" className="text-xs text-muted-foreground">{count} selected</span>
      <div className="flex flex-wrap gap-1">
        <Button type="button" variant="ghost" size="sm" disabled={disabled || !eligible.some(option => !selected.has(option.id))} onClick={() => onChange(new Set([...selected, ...eligible.map(option => option.id)]))}>{query ? 'Select all results' : 'Select all'}</Button>
        <Button type="button" variant="ghost" size="sm" disabled={disabled || count === 0} onClick={() => onChange(new Set())}>Clear selection</Button>
      </div>
    </div>
    <div className="max-h-64 overflow-y-auto border-y border-border divide-y divide-border">
      {matches.map(option => <div key={option.id} className="flex flex-wrap items-center gap-3 px-2 py-2 sm:flex-nowrap">
        <label className={`flex min-h-[56px] min-w-0 flex-1 items-center gap-3 ${option.disabled ? 'opacity-60' : 'cursor-pointer hover:bg-muted/50'}`}>
        <Checkbox aria-label={option.name} checked={!option.disabled && selected.has(option.id)} disabled={disabled || option.disabled} onCheckedChange={checked => {
          const next = new Set(selected)
          if (checked === true) next.add(option.id)
          else next.delete(option.id)
          onChange(next)
        }} />
        <Avatar className="h-8 w-8 shrink-0">
          {option.avatar && <AvatarImage src={option.avatar} alt="" />}
          <AvatarFallback>{option.name.slice(0, 1).toUpperCase()}</AvatarFallback>
        </Avatar>
        <span className="min-w-0 flex-1">
          <span className="block break-words text-sm font-medium">{option.name}</span>
          <span className="block break-words text-xs text-muted-foreground">{option.description || option.email || 'Workspace member'}</span>
        </span>
      </label>
        {renderAction && <div className="w-full pl-11 sm:w-auto sm:shrink-0 sm:pl-0">{renderAction(option)}</div>}
      </div>)}
      {!matches.length && <p className="p-4 text-sm text-muted-foreground">No members found.</p>}
    </div>
  </div>
}

export function MemberMoveConfirmation({ moves, confirmed, onChange, disabled }: {
  moves: string[]
  confirmed: boolean
  onChange: (confirmed: boolean) => void
  disabled?: boolean
}) {
  if (!moves.length) return null
  return <div className="space-y-2 border border-border bg-muted/30 p-3 text-sm">
    <p>These people will leave their current team:</p>
    <ul className="list-disc pl-5">{moves.map((move, index) => <li key={`${index}:${move}`}>{move}</li>)}</ul>
    <label className="flex cursor-pointer items-start gap-2">
      <Checkbox className="mt-0.5" checked={confirmed} disabled={disabled} onCheckedChange={checked => onChange(checked === true)} />
      <span>I confirm moving these members to this team.</span>
    </label>
  </div>
}
